import React, { useEffect, useState } from "react";
import api, { resolveImageUrl } from "@/lib/api";
import { Plus, Pencil, Trash2, X, ExternalLink, Eye, EyeOff, Copy, Boxes, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";

const EMPTY = () => ({
  slug: "",
  name: "",
  full_name: "",
  tagline: "",
  short_description: "",
  long_description: "",
  category: "Software",
  color: "#0056A4",
  featured_image: "",
  logo_image: "",
  gallery: [],
  features: [],
  modules: [],
  pricing_plans: [],
  on_premise_note: "",
  on_premise_price: "",
  demo_url: "",
  demo_label: "Open Demo",
  demo_visible: true,
  free_trial_available: true,
  is_active: true,
  is_featured: false,
  order: 0,
});

// A pre-populated template scaffold so admins can spin up new solutions fast.
const TEMPLATE = () => ({
  ...EMPTY(),
  slug: "",
  name: "",
  full_name: "",
  tagline: "A short, benefit-focused statement.",
  short_description: "One-sentence description that appears on cards and grids.",
  long_description: "Write a 3–5 sentence overview describing what the solution does, who it's for, and the outcomes it drives.",
  category: "Software",
  features: ["Key feature one", "Key feature two", "Key feature three"],
  modules: [
    { name: "Module 1", description: "Describe what this module does." },
    { name: "Module 2", description: "Describe what this module does." },
  ],
  pricing_plans: [
    { name: "STARTER", audience: "Small teams", price: "₱0/mo", setup_fee: "Free" },
    { name: "GROWTH", audience: "Growing teams", price: "Custom", setup_fee: "Scoped" },
  ],
  on_premise_note: "On-premise deployment is available for organizations that need dedicated infrastructure and data control. Implementation and customization are scoped separately.",
  on_premise_price: "Scoped based on requirements",
  demo_label: "Request Demo",
  demo_visible: false,
});

function duplicateOf(s) {
  const copy = { ...s };
  delete copy.id;
  copy.slug = `${s.slug}-copy`;
  copy.name = `${s.name} (Copy)`;
  copy.is_active = false;
  copy.is_featured = false;
  copy.order = (s.order || 0) + 100;
  return copy;
}

export default function AdminSolutions() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/solutions").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing(TEMPLATE());
  const startBlank = () => setEditing(EMPTY());
  const startEdit = (s) => setEditing({ ...EMPTY(), ...s });
  const startDuplicate = (s) => {
    setEditing(duplicateOf({ ...EMPTY(), ...s }));
    toast.info(`Duplicated "${s.name}" as template — edit & save to publish.`);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this solution?")) return;
    await api.delete(`/admin/solutions/${id}`);
    toast.success("Solution deleted");
    load();
  };

  const save = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await api.put(`/admin/solutions/${editing.id}`, editing);
        toast.success("Solution updated");
      } else {
        await api.post(`/admin/solutions`, editing);
        toast.success("Solution created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div data-testid="admin-solutions-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-2">Solutions</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Manage Solutions</h1>
          <p className="text-sm text-msr-slate mt-2 max-w-2xl">Create new solutions from a pre-filled template, duplicate an existing one to keep the same structure, or build from a blank canvas.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={startBlank} className="btn-msr-outline py-2.5 px-4 text-xs" data-testid="admin-solutions-add-blank-btn">
            <Plus size={14} /> Blank
          </button>
          <button onClick={startNew} className="btn-msr-primary" data-testid="admin-solutions-add-btn">
            <Sparkles size={14} /> Add Solution (from template)
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-msr-line p-12 text-center" data-testid="admin-solutions-empty">
          <Boxes size={36} className="text-msr-slate mx-auto mb-4" />
          <h2 className="font-heading font-extrabold text-xl mb-2">No solutions yet</h2>
          <p className="text-msr-slate mb-6">Start by creating your first solution using the MSR template.</p>
          <button onClick={startNew} className="btn-msr-primary"><Sparkles size={14} /> Create your first solution</button>
        </div>
      ) : (
      <div className="bg-white border border-msr-line overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-msr-mist text-left text-[11px] uppercase tracking-wider text-msr-slate">
            <tr>
              <th className="px-4 py-3">Solution</th>
              <th className="px-4 py-3">Logo</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Demo</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-msr-line" data-testid={`admin-solution-row-${s.slug}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-msr-mist overflow-hidden flex-shrink-0">
                      {s.featured_image && <img src={resolveImageUrl(s.featured_image)} className="h-full w-full object-cover" alt="" />}
                    </div>
                    <div>
                      <div className="font-semibold">{s.name}</div>
                      <div className="text-xs text-msr-slate">{s.full_name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {s.logo_image ? (
                    <div className="h-10 w-16 bg-msr-ink/95 grid place-items-center p-1" data-testid={`admin-solution-logo-${s.slug}`}>
                      <img src={resolveImageUrl(s.logo_image)} className="h-full w-full object-contain" alt="logo" />
                    </div>
                  ) : (
                    <button onClick={() => startEdit(s)} className="h-10 w-16 border-2 border-dashed border-msr-line text-[10px] text-msr-slate hover:border-msr-blue hover:text-msr-blue" data-testid={`admin-solution-logo-add-${s.slug}`}>
                      + Logo
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-msr-slate">{s.category}</td>
                <td className="px-4 py-3">
                  {s.demo_visible ? <span className="badge-soft-green"><Eye size={10} /> Visible</span> : <span className="badge-soft-blue"><EyeOff size={10} /> Hidden</span>}
                  {s.demo_url && (
                    <a href={s.demo_url} target="_blank" rel="noreferrer" className="text-msr-blue ml-2 inline-flex items-center gap-1 text-xs"><ExternalLink size={10} /> Open</a>
                  )}
                </td>
                <td className="px-4 py-3">{s.is_active ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(s)} className="text-msr-blue inline-flex items-center gap-1 text-xs mr-3" data-testid={`admin-solution-edit-${s.slug}`}>
                    <Pencil size={12} /> Edit
                  </button>
                  <button onClick={() => startDuplicate(s)} className="text-msr-green-dark inline-flex items-center gap-1 text-xs mr-3" data-testid={`admin-solution-duplicate-${s.slug}`} title="Duplicate as template">
                    <Copy size={12} /> Duplicate
                  </button>
                  <button onClick={() => remove(s.id)} className="text-red-500 inline-flex items-center gap-1 text-xs" data-testid={`admin-solution-delete-${s.slug}`}>
                    <Trash2 size={12} /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {editing && <SolutionEditor value={editing} onChange={setEditing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function SolutionEditor({ value, onChange, onSave, onClose }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setList = (k, items) => onChange({ ...value, [k]: items });

  return (
    <div className="fixed inset-0 z-40 bg-black/60 grid place-items-center p-4" data-testid="admin-solution-editor">
      <div className="bg-white w-full max-w-5xl max-h-[92vh] overflow-y-auto border-t-4 border-msr-blue relative">
        <div className="sticky top-0 bg-white border-b border-msr-line px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="overline">{value.id ? "Editing solution" : "New solution"}</div>
            <div className="font-heading font-bold text-lg">{value.name || "New Solution"}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={onSave} className="btn-msr-primary py-2 px-4 text-xs" data-testid="admin-solution-save-btn">Save</button>
            <button onClick={onClose} className="p-2 text-msr-slate hover:text-msr-ink" data-testid="admin-solution-close-btn"><X size={20} /></button>
          </div>
        </div>
        <div className="p-6 space-y-8">
          <Section title="01 — Identity" subtitle="The basics that identify this solution across the site.">
            <div className="grid lg:grid-cols-2 gap-5">
              <Field label="Slug *" value={value.slug} onChange={(v) => set("slug", v)} placeholder="e.g. tipon" testId="sol-edit-slug" />
              <Field label="Name *" value={value.name} onChange={(v) => set("name", v)} placeholder="e.g. TIPON" testId="sol-edit-name" />
              <Field label="Full Name" value={value.full_name} onChange={(v) => set("full_name", v)} placeholder="e.g. Total Information & Paperless Organization Network" testId="sol-edit-full-name" />
              <Field label="Tagline" value={value.tagline} onChange={(v) => set("tagline", v)} testId="sol-edit-tagline" />
              <Field label="Category" value={value.category} onChange={(v) => set("category", v)} placeholder="e.g. Document Management" testId="sol-edit-category" />
              <Field label="Color (hex)" value={value.color} onChange={(v) => set("color", v)} placeholder="#0056A4" testId="sol-edit-color" />
            </div>
          </Section>

          <Section title="02 — Brand & Media" subtitle="Upload the solution's logo and the featured image used in cards and on the detail page hero. PNG with transparent background recommended for the logo.">
            <div className="grid lg:grid-cols-2 gap-5">
              <ImageUploader label="Logo image (transparent PNG preferred)" value={value.logo_image} onChange={(v) => set("logo_image", v)} testId="sol-edit-logo" />
              <ImageUploader label="Featured image (cover photo, ~1600px wide)" value={value.featured_image} onChange={(v) => set("featured_image", v)} testId="sol-edit-featured-image" />
            </div>
            <div className="mt-5">
              <GalleryEditor items={value.gallery} onChange={(items) => setList("gallery", items)} />
            </div>
          </Section>

          <Section title="03 — Content" subtitle="Marketing copy that explains what this solution does.">
            <div className="space-y-4">
              <TextArea label="Short description (appears on grids/cards)" value={value.short_description} onChange={(v) => set("short_description", v)} testId="sol-edit-short" />
              <TextArea label="Long description (overview section)" value={value.long_description} onChange={(v) => set("long_description", v)} rows={5} testId="sol-edit-long" />
            </div>
          </Section>

          <Section title="04 — Features & Modules" subtitle="Highlight key features and break down the solution into modules.">
            <StringListEditor label="Key features" items={value.features} onChange={(items) => setList("features", items)} testId="sol-edit-features" />
            <div className="mt-6">
              <ModulesEditor items={value.modules} onChange={(items) => setList("modules", items)} />
            </div>
          </Section>

          <Section title="05 — Pricing Plans" subtitle="Define plan tiers shown on the solution detail page.">
            <PricingEditor items={value.pricing_plans} onChange={(items) => setList("pricing_plans", items)} />
          </Section>

          <Section title="06 — On-Premise Deployment" subtitle="Optional on-prem section shown near the bottom of the solution detail page.">
            <div className="grid lg:grid-cols-2 gap-5">
              <Field label="On-premise price label" value={value.on_premise_price} onChange={(v) => set("on_premise_price", v)} placeholder="e.g. Starts at ₱100,000" testId="sol-edit-onprem-price" />
              <div />
              <div className="lg:col-span-2">
                <TextArea label="On-premise note" value={value.on_premise_note} onChange={(v) => set("on_premise_note", v)} testId="sol-edit-onprem-note" />
              </div>
            </div>
          </Section>

          <Section title="07 — Demo & CTAs" subtitle="Configure the demo button, free-trial CTA, and where it points.">
            <div className="grid lg:grid-cols-2 gap-5">
              <Field label="Demo URL" value={value.demo_url} onChange={(v) => set("demo_url", v)} placeholder="https://demo.example.com" testId="sol-edit-demo-url" />
              <Field label="Demo button label" value={value.demo_label} onChange={(v) => set("demo_label", v)} placeholder="Open Demo" testId="sol-edit-demo-label" />
              <ToggleField label="Demo button visible" value={value.demo_visible} onChange={(v) => set("demo_visible", v)} testId="sol-edit-demo-visible" />
              <ToggleField label="Free trial available" value={value.free_trial_available} onChange={(v) => set("free_trial_available", v)} testId="sol-edit-free-trial" />
            </div>
          </Section>

          <Section title="08 — Visibility & Order" subtitle="Control whether this solution appears publicly and where.">
            <div className="grid lg:grid-cols-2 gap-5">
              <ToggleField label="Active (visible on site)" value={value.is_active} onChange={(v) => set("is_active", v)} testId="sol-edit-active" />
              <ToggleField label="Featured on homepage" value={value.is_featured} onChange={(v) => set("is_featured", v)} testId="sol-edit-featured" />
              <Field label="Order (lower = appears first)" type="number" value={value.order} onChange={(v) => set("order", Number(v))} testId="sol-edit-order" />
            </div>
          </Section>

          <div className="sticky bottom-0 bg-white border-t border-msr-line py-4 -mx-6 px-6 flex justify-end gap-2">
            <button onClick={onClose} className="text-sm px-4 py-2 border border-msr-line" data-testid="admin-solution-cancel-btn">Cancel</button>
            <button onClick={onSave} className="btn-msr-primary py-2.5 px-5 text-xs" data-testid="admin-solution-save-bottom-btn">Save Solution</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="pb-6 border-b border-msr-line last:border-0 last:pb-0">
      <div className="mb-4">
        <div className="overline">{title}</div>
        {subtitle && <p className="text-sm text-msr-slate mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function GalleryEditor({ items, onChange }) {
  const add = () => onChange([...(items || []), ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Gallery images</div>
        <button onClick={add} className="text-xs bg-msr-blue text-white px-3 py-1.5 inline-flex items-center gap-1" data-testid="sol-gallery-add"><Plus size={12} /> Add image</button>
      </div>
      <div className="space-y-3">
        {(items || []).map((img, i) => (
          <div key={i} className="flex items-start gap-3" data-testid={`sol-gallery-item-${i}`}>
            <div className="flex-1"><ImageUploader value={img} onChange={(v) => update(i, v)} label={`Gallery image ${i + 1}`} testId={`sol-gallery-uploader-${i}`} /></div>
            <button onClick={() => remove(i)} className="text-red-500 mt-7" data-testid={`sol-gallery-remove-${i}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModulesEditor({ items, onChange }) {
  const add = () => onChange([...(items || []), { name: "", description: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Modules</div>
        <button onClick={add} className="text-xs bg-msr-blue text-white px-3 py-1.5 inline-flex items-center gap-1" data-testid="sol-modules-add"><Plus size={12} /> Add module</button>
      </div>
      <div className="space-y-3">
        {(items || []).map((m, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 border border-msr-line p-3" data-testid={`sol-module-${i}`}>
            <input value={m.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Module name" className="col-span-3 border border-msr-line px-2 py-2 text-sm" data-testid={`sol-module-name-${i}`} />
            <input value={m.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Description" className="col-span-8 border border-msr-line px-2 py-2 text-sm" data-testid={`sol-module-desc-${i}`} />
            <button onClick={() => remove(i)} className="text-red-500 col-span-1 grid place-items-center" data-testid={`sol-module-remove-${i}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingEditor({ items, onChange }) {
  const add = () => onChange([...(items || []), { name: "", audience: "", price: "", setup_fee: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Pricing plans</div>
        <button onClick={add} className="text-xs bg-msr-blue text-white px-3 py-1.5 inline-flex items-center gap-1" data-testid="sol-plans-add"><Plus size={12} /> Add plan</button>
      </div>
      <div className="space-y-3">
        {(items || []).map((p, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 border border-msr-line p-3" data-testid={`sol-plan-${i}`}>
            <input value={p.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Plan" className="col-span-2 border border-msr-line px-2 py-2 text-sm" data-testid={`sol-plan-name-${i}`} />
            <input value={p.audience} onChange={(e) => update(i, "audience", e.target.value)} placeholder="Audience" className="col-span-4 border border-msr-line px-2 py-2 text-sm" data-testid={`sol-plan-audience-${i}`} />
            <input value={p.price} onChange={(e) => update(i, "price", e.target.value)} placeholder="Price" className="col-span-3 border border-msr-line px-2 py-2 text-sm" data-testid={`sol-plan-price-${i}`} />
            <input value={p.setup_fee} onChange={(e) => update(i, "setup_fee", e.target.value)} placeholder="Setup fee" className="col-span-2 border border-msr-line px-2 py-2 text-sm" data-testid={`sol-plan-setup-${i}`} />
            <button onClick={() => remove(i)} className="text-red-500 col-span-1 grid place-items-center" data-testid={`sol-plan-remove-${i}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function StringListEditor({ label, items, onChange, testId }) {
  const add = () => onChange([...(items || []), ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{label}</div>
        <button onClick={add} className="text-xs bg-msr-blue text-white px-3 py-1.5 inline-flex items-center gap-1" data-testid={`${testId}-add`}><Plus size={12} /> Add</button>
      </div>
      <div className="space-y-2">
        {(items || []).map((v, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`${testId}-item-${i}`}>
            <input value={v} onChange={(e) => update(i, e.target.value)} className="flex-1 border border-msr-line px-3 py-2 text-sm" />
            <button onClick={() => remove(i)} className="text-red-500" data-testid={`${testId}-remove-${i}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <input type={type} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue" data-testid={testId} />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full border border-msr-line px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue resize-none" data-testid={testId} />
    </label>
  );
}

function ToggleField({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-3 py-2.5" data-testid={testId}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-msr-blue" />
      <span className="text-sm font-semibold text-msr-ink">{label}</span>
    </label>
  );
}
